(function () {
  'use strict';

  // Confirmed via live Console diagnostics on the actual Shopify preview,
  // inspected cell-by-cell: Flickity caches each cell's measured height once
  // (Cell.prototype.getSize(), which reads element.offsetHeight) the first
  // time it sizes that cell, and nothing re-measures it on its own.
  // flkty.maxCellHeight (the max of every cell's cached outerHeight) and the
  // gallery height flkty.setGallerySize() applies to .flickity-viewport stay
  // wrong indefinitely once that first measurement is wrong -- confirmed
  // live: flkty.maxCellHeight stuck at 60.5938px while real cards were
  // 364-386px. flkty.resize() itself is the correct, complete recovery
  // method (verified directly in vendor.min.js: it runs getSize() ->
  // positionCells() -> _sizeCells() -> each cell.getSize() -> recomputes
  // maxCellHeight -> setGallerySize() -> applies the fresh height to
  // .flickity-viewport, re-measuring the live DOM every time, never a
  // hard-coded value), and was proven to correctly recover the exact
  // confirmed 60.5938 state back to ~421px.
  //
  // Rather than guessing when to call it via arbitrary fixed delays, this
  // detects the actual, measurable problem directly: compare a live,
  // Flickity-independent measurement (getBoundingClientRect().height on
  // every .carousel__slide -- fractional and precise, not offsetHeight's
  // rounded integer, and independent of flkty.maxCellHeight/cell.size,
  // which are exactly the values being verified) against Flickity's own
  // cached flkty.maxCellHeight. If the live measurement is genuinely taller
  // (beyond a small tolerance for normal sub-pixel noise), Flickity's cache
  // is stale by definition, regardless of why or when that happened.
  //
  // Live testing on the actual Shopify draft preview showed this could
  // still remain visibly broken after flkty.resize() was called, for a
  // reason not pinned down despite extensive attempts. Because of that,
  // the visible outcome is no longer made to depend on resize() succeeding:
  // the moment a mismatch is detected, .flickity-viewport's height is set
  // directly from the same live measurement (see forceViewportHeight
  // below), guaranteeing the customer-visible result regardless of
  // Flickity's own internal state. resize() is still attempted afterward,
  // with retries, purely to keep Flickity's own internal cache consistent
  // for later interactions (arrows, tab switches) -- it is no longer the
  // only thing standing between the customer and a broken layout.
  // Measures each slide's true content height directly from its image
  // wrapper and info block, NOT from .carousel__slide's own
  // getBoundingClientRect(). This distinction matters: confirmed directly,
  // once .flickity-viewport already has an explicit (even a wrong) height,
  // .carousel__slide's own measured height is no longer independent of it
  // -- the theme's shared .products.carousel.flickity-enabled.flickity-resize
  // .carousel__slide{min-height:100%} rule (previously tested and correctly
  // ruled out in the case where the viewport had NO explicit height at all)
  // inherits that explicit height once one exists, making the slide's own
  // measured height equal the very (possibly wrong) value being verified --
  // a circular, self-confirming measurement that can never detect a
  // too-tall viewport. Measuring from the top of the image wrapper to the
  // bottom of the info block instead reflects the two children's own
  // normal-flow layout, which is unaffected by the parent's inherited
  // height, giving a true, independent reading in both directions.
  function getLiveMaxSlideHeight(panel) {
    var slides = panel.querySelectorAll('.carousel__slide');
    var max = 0;
    for (var i = 0; i < slides.length; i++) {
      var wrapper = slides[i].querySelector('.product-card--featured-image-link');
      var info = slides[i].querySelector('.product-card-info');
      var h;
      if (wrapper && info) {
        h = info.getBoundingClientRect().bottom - wrapper.getBoundingClientRect().top;
      } else {
        h = slides[i].getBoundingClientRect().height;
      }
      if (h > max) max = h;
    }
    return max;
  }

  var HEIGHT_TOLERANCE_PX = 2;
  var MAX_RECOVERY_ATTEMPTS = 5;

  // Direct, guaranteed correction of what is actually visible, independent
  // of Flickity's own internal cache. flkty.resize() has been proven, in
  // repeated isolated testing, to correctly recompute Flickity's internal
  // state -- but live evidence on the actual Shopify draft preview has now
  // shown the visible result can still remain wrong afterward, for a reason
  // that has not been pinned down despite extensive attempts. Rather than
  // continuing to trust resize() alone to fix what the customer sees, this
  // sets .flickity-viewport's own height directly from the same live,
  // Flickity-independent measurement used to detect the mismatch in the
  // first place. This never hard-codes a number -- "live" is re-measured
  // fresh every time this runs -- it simply stops depending on Flickity's
  // own recalculation succeeding for the visible outcome. Flickity's
  // horizontal cell positioning (arrows, dragging) is untouched by this,
  // since that operates via .flickity-slider's transform, not the
  // viewport's height.
  function forceViewportHeight(panel, live) {
    var slider = panel.querySelector('.carousel');
    if (!slider) return;
    var viewport = slider.querySelector('.flickity-viewport');
    if (!viewport) return;
    var current = viewport.getBoundingClientRect().height;
    if (Math.abs(current - live) > HEIGHT_TOLERANCE_PX) {
      viewport.style.height = live + 'px';
    }
  }

  // Verifies the actual invariant and, only if it's violated, immediately
  // guarantees the correct visible result (forceViewportHeight), then
  // separately still attempts flkty.resize() so Flickity's own internal
  // state (used by arrows/dragging/future tab switches) stays consistent
  // with what is now visible. The resize()-based part retries up to a
  // small, fixed number of attempts and stops the moment the invariant
  // holds; it can never run forever since the counter is a hard ceiling
  // regardless of outcome. panel._ggRecovering prevents more than one such
  // chain running concurrently on the same panel if multiple trigger points
  // (e.g. a tab click during the window 'load' event) fire close together.
  //
  // Confirmed via live recording on the actual Shopify draft preview: the
  // "VIEW ALL" button could end up separated from the products by a large
  // empty gap, even though titles/prices were all correctly visible -- a
  // different symptom from the original clipping bug, but the same root
  // mechanism. The comparison below previously checked
  // "live > cached + tolerance" using flkty.maxCellHeight as "cached" --
  // one-directional, and against the wrong value. It could only ever catch
  // the viewport being too SHORT, never too TALL, and it compared against
  // Flickity's own internal cache rather than the viewport's own actual
  // rendered height -- the specific DOM value forceViewportHeight controls
  // and the one the customer actually sees. Once forceViewportHeight had
  // set that height once (from any transient over-measurement -- e.g. a
  // hover-zoom transform or a mid-transition read), nothing before this fix
  // ever re-checked whether it should now be shrunk back down, since
  // flkty.maxCellHeight and the viewport's own inline height are two
  // separate values that forceViewportHeight does not keep in sync. Fixed
  // by comparing directly against the viewport's own current rendered
  // height, in both directions.
  function recoverIfMismatched(panel, attemptsLeft) {
    if (!panel || attemptsLeft <= 0) {
      if (panel) panel._ggRecovering = false;
      return;
    }
    var slider = panel.querySelector('.carousel');
    if (!slider || typeof window.Flickity !== 'function') {
      panel._ggRecovering = false;
      return;
    }
    var flkty = window.Flickity.data(slider);
    if (!flkty || typeof flkty.resize !== 'function') {
      panel._ggRecovering = false;
      return;
    }
    var viewport = slider.querySelector('.flickity-viewport');
    if (!viewport) {
      panel._ggRecovering = false;
      return;
    }

    var live = getLiveMaxSlideHeight(panel);
    if (live <= 0) {
      // Nothing rendered yet to compare against -- not a mismatch, just
      // not ready; stop this chain without forcing anything blindly.
      panel._ggRecovering = false;
      return;
    }

    var currentViewportHeight = viewport.getBoundingClientRect().height;
    var mismatched = Math.abs(live - currentViewportHeight) > HEIGHT_TOLERANCE_PX;

    if (!mismatched) {
      panel._ggRecovering = false;
      return;
    }

    // Fix what is visible right now, unconditionally, before attempting
    // Flickity's own recalculation -- the customer-facing result no longer
    // depends on whether the call below succeeds.
    forceViewportHeight(panel, live);

    panel._ggRecovering = true;

    function afterResizeAttempt() {
      window.requestAnimationFrame(function () {
        recoverIfMismatched(panel, attemptsLeft - 1);
      });
    }

    if (flkty.isAnimating) {
      if (flkty._ggResizePending) return;
      flkty._ggResizePending = true;
      flkty.once('settle', function () {
        flkty._ggResizePending = false;
        flkty.resize();
        afterResizeAttempt();
      });
    } else {
      flkty.resize();
      afterResizeAttempt();
    }
  }

  function resizePanelCarousel(panel) {
    if (!panel) return;
    if (panel._ggRecovering) return;
    recoverIfMismatched(panel, MAX_RECOVERY_ATTEMPTS);
  }


  // ---------------------------------------------------------------------
  // Lazy tabs (6 Oct 2026, speed).
  //
  // Only the first tab's product cards come with the page. Every other
  // panel holds an empty .gg-carousel__lazy placeholder with its
  // collection's address (data-gg-src) and the block's "Products to show"
  // (data-gg-limit). The first time a tab is opened, the cards are fetched
  // from sections/ws-gg-panel.liquid through Shopify's Section Rendering API
  // (/collections/<handle>?section_id=ws-gg-panel), 12 cards per page -- a
  // second page is requested only when the tab is set to show more than 12 --
  // trimmed to the limit and put in place of the placeholder. The <slide-show> element upgrades
  // itself the moment it is connected, exactly as it does on page load.
  //
  // The request starts as soon as the pointer or keyboard reaches a tab
  // (pointerenter / focus / touchstart), so by the click it is usually done.
  // If it fails, the placeholder is simply removed: the tab still shows its
  // "View all" button, which links to the same collection.
  // ---------------------------------------------------------------------
  var panelRequests = {};

  var CARDS_PER_PAGE = 12; // must match {% paginate ... by 12 %} in sections/ws-gg-panel.liquid

  function sectionUrl(src, page) {
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'section_id=ws-gg-panel' + (page > 1 ? '&page=' + page : '');
  }

  function fetchText(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (response) {
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return response.text();
    });
  }

  // Resolves to an array of HTML strings, one per page needed for the limit.
  function requestPanel(lazy) {
    var src = lazy && lazy.getAttribute('data-gg-src');
    if (!src) return null;
    var limit = parseInt(lazy.getAttribute('data-gg-limit'), 10) || CARDS_PER_PAGE;
    var pages = Math.max(1, Math.ceil(limit / CARDS_PER_PAGE));
    var key = src + '|' + pages;
    if (!panelRequests[key]) {
      var requests = [];
      for (var page = 1; page <= pages; page++) requests.push(fetchText(sectionUrl(src, page)));
      panelRequests[key] = Promise.all(requests).catch(function (error) {
        delete panelRequests[key];
        throw error;
      });
    }
    return panelRequests[key];
  }

  function prefetchPanel(root, tab) {
    var panel = root.querySelector('[data-gg-panel="' + tab.getAttribute('data-gg-tab') + '"]');
    var lazy = panel && panel.querySelector('.gg-carousel__lazy[data-gg-src]');
    if (lazy) {
      var request = requestPanel(lazy);
      if (request) request.catch(function () {});
    }
  }

  function loadPanel(panel, placeholderHeight) {
    var lazy = panel && panel.querySelector('.gg-carousel__lazy[data-gg-src]');
    if (!lazy || lazy._ggLoading) return;
    lazy._ggLoading = true;

    // Hold the space the previous tab's slider took, so the "View all"
    // button and everything below do not jump up and back down.
    if (placeholderHeight > 0) lazy.style.minHeight = placeholderHeight + 'px';
    lazy.setAttribute('aria-busy', 'true');

    var request = requestPanel(lazy);
    if (!request) return;

    request.then(function (pages) {
      var holder = document.createElement('template');
      holder.innerHTML = pages[0];
      var slider = holder.content.querySelector('slide-show');
      if (!slider) throw new Error('No slider in response');

      // Cards from any further page go in after the first page's cards,
      // ahead of the two arrows.
      var firstNav = slider.querySelector('.flickity-nav');
      for (var p = 1; p < pages.length; p++) {
        var extra = document.createElement('template');
        extra.innerHTML = pages[p];
        extra.content.querySelectorAll('.carousel__slide').forEach(function (slide) {
          slider.insertBefore(slide, firstNav);
        });
      }

      var id = panel.getAttribute('data-gg-panel');
      var limit = parseInt(lazy.getAttribute('data-gg-limit'), 10) || CARDS_PER_PAGE;
      var slides = slider.querySelectorAll('.carousel__slide');
      for (var i = limit; i < slides.length; i++) {
        slides[i].parentNode.removeChild(slides[i]);
      }
      slider.classList.remove('gg-carousel__slider--lazy');
      slider.classList.add('gg-carousel__slider--' + id);
      slider.setAttribute('data-gg-slider', id);

      lazy.parentNode.replaceChild(slider, lazy);

      // The currency converter app rewrites prices once, on page load;
      // ask it to convert the prices that just arrived as well.
      if (window.bucksCC && typeof window.bucksCC.reConvert === 'function') {
        try { window.bucksCC.reConvert(); } catch (e) {}
      }

      window.requestAnimationFrame(function () {
        resizePanelCarousel(panel);
      });
      [300, 800, 1500, 2500].forEach(function (delay) {
        window.setTimeout(function () {
          if (panel.classList.contains('is-active')) resizePanelCarousel(panel);
        }, delay);
      });
    }).catch(function () {
      if (lazy.parentNode) lazy.parentNode.removeChild(lazy);
    });
  }

  function init(root) {
    if (!root || root.dataset.ggTabsReady === 'true') return;
    root.dataset.ggTabsReady = 'true';

    root.addEventListener('click', function (event) {
      var tab = event.target.closest('[data-gg-tab]');
      if (!tab || !root.contains(tab)) return;

      var category = tab.getAttribute('data-gg-tab');
      var previousPanel = root.querySelector('[data-gg-panel].is-active');
      var previousHeight = previousPanel ? previousPanel.getBoundingClientRect().height : 0;

      root.querySelectorAll('[data-gg-tab]').forEach(function (button) {
        var active = button === tab;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      var activePanel = null;
      root.querySelectorAll('[data-gg-panel]').forEach(function (panel) {
        var isActive = panel.getAttribute('data-gg-panel') === category;
        panel.classList.toggle('is-active', isActive);
        if (isActive) activePanel = panel;
      });

      if (activePanel && activePanel.querySelector('.gg-carousel__lazy[data-gg-src]')) {
        var button = activePanel.querySelector('.gg-carousel__button-wrapper');
        var buttonHeight = button ? button.getBoundingClientRect().height : 0;
        loadPanel(activePanel, previousHeight - buttonHeight);
        return;
      }

      window.requestAnimationFrame(function () {
        resizePanelCarousel(activePanel);
      });
    });

    // Start downloading a tab's cards as soon as it is about to be used.
    ['pointerenter', 'focusin', 'touchstart'].forEach(function (type) {
      root.addEventListener(type, function (event) {
        var tab = event.target && event.target.closest && event.target.closest('[data-gg-tab]');
        if (tab && root.contains(tab)) prefetchPanel(root, tab);
      }, { passive: true, capture: type === 'pointerenter' });
    });

    // The panel that is active by default on page load never receives a
    // click, so give its own Flickity instance the same invariant check
    // once, right after setup.
    window.requestAnimationFrame(function () {
      resizePanelCarousel(root.querySelector('[data-gg-panel].is-active'));
    });

    // Real product images can finish loading well after Flickity's own
    // initial measurement. window's 'load' event is the correct, semantic
    // signal for "every resource on the page, including every image, has
    // now finished" -- not a guessed delay. Re-checking the invariant here
    // catches any card whose true height only became visible once its
    // image (or anything else) finished settling.
    window.addEventListener('load', function () {
      resizePanelCarousel(root.querySelector('[data-gg-panel].is-active'));
    });

    // Live testing on the actual Shopify draft preview showed the failure
    // persisting across reloads despite the triggers above. As additional,
    // unconditional insurance -- not a replacement for the invariant checks
    // above, which remain the primary mechanism -- re-run the same
    // Flickity-independent check at a few points across the first couple of
    // seconds after setup. Each call is the same cheap, safe, idempotent
    // check: it only acts if a real mismatch is still measured at that
    // moment, using forceViewportHeight's own live re-measurement, never a
    // fixed number.
    [300, 800, 1500, 2500].forEach(function (delay) {
      window.setTimeout(function () {
        resizePanelCarousel(root.querySelector('[data-gg-panel].is-active'));
      }, delay);
    });
  }

  function boot() {
    document.querySelectorAll('.gg-carousel').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Root cause of the live, intermittent failure: this file's own <script>
  // tag lives inside the Gaurav section's own HTML. Shopify's Theme Editor
  // re-renders a section's markup via an innerHTML-style replacement when a
  // merchant edits its settings/blocks (confirmed against this theme's own
  // established convention: collection-view-toggle.js, theme-editor.js, and
  // ws-mobile-zuria-image-carousel.js all already listen for this exact
  // event for exactly this reason). <script> tags inserted via innerHTML
  // never execute -- this is standard browser behavior, not a bug -- so
  // after such a refresh this file's own top-level code never runs again on
  // the fresh markup, even though the <slide-show> custom element inside it
  // still upgrades and constructs a brand new Flickity instance (custom
  // element upgrade is a DOM-level mechanism, independent of script
  // execution). That new instance never receives its tab-click listener or
  // its initial resize correction, since nothing ever re-runs init() on it.
  //
  // Shopify's own documented mechanism for this is the 'shopify:section:load'
  // event, dispatched on the section's own wrapper element every time the
  // Theme Editor re-renders it -- listening for it on `document` is safe
  // regardless of whether this section's own script tag ever re-executes,
  // since `document` itself is never replaced. This matches the exact
  // pattern already used elsewhere in this same theme.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('.gg-carousel');
    if (root) init(root);
  });
}());
